import { captureException } from '@sentry/react';
import { Duration, DurationLikeObject } from 'luxon';
import { I18n } from '../providers';

type UnitDisplay = 'long' | 'short' | 'narrow';
type DurationRoundMode = 'nearest' | 'floor' | 'ceil';

interface FormatDurationOptions {
  round?: boolean | DurationRoundMode;
  roundTo?: keyof DurationLikeObject;
}

const MINUTES_BY_UNIT: Partial<Record<keyof DurationLikeObject, number>> = {
  days: 1_440,
  hours: 60,
  milliseconds: 1 / 60_000,
  minutes: 1,
  seconds: 1 / 60,
  weeks: 10_080,
};

const toRoundedMinutes = (minutes: number, shiftTo: Array<keyof DurationLikeObject>, options?: FormatDurationOptions): number => {
  if (!options?.round) return minutes;

  const roundMode = options.round === true ? 'nearest' : options.round;
  const targetUnit = options.roundTo ?? shiftTo[0] ?? 'hours';
  const step = MINUTES_BY_UNIT[targetUnit];
  if (!step) return minutes;

  const valueInTargetUnit = minutes / step;
  if (roundMode === 'floor') return Math.floor(valueInTargetUnit) * step;
  if (roundMode === 'ceil') return Math.ceil(valueInTargetUnit) * step;
  return Math.round(valueInTargetUnit) * step;
};

export const formatDuration = (
  minutes: number,
  display: UnitDisplay = 'long',
  shiftTo: Array<keyof DurationLikeObject> = ['hours', 'minutes'],
  options?: FormatDurationOptions,
): string => {
  try {
    const roundedMinutes = toRoundedMinutes(minutes, shiftTo, options);
    return Duration.fromObject({ minutes: roundedMinutes }, { locale: I18n.getInstance().language }).shiftTo(...shiftTo).toHuman({ unitDisplay: display });
  } catch (error) {
    captureException(error);
    return minutes.toString();
  }
};
